import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Link,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CheckCircleOutlined as CheckIcon,
  EmailOutlined as EmailIcon,
  LoginOutlined as LoginIcon,
  BusinessOutlined as OrgIcon,
  ReceiptLongOutlined as ReceiptIcon,
  MenuBookOutlined as DocsIcon,
  HelpOutlineOutlined as HelpIcon,
  WorkspacePremiumOutlined as PlanIcon,
  RocketLaunchOutlined as RocketIcon,
  GroupAddOutlined as TeamIcon,
  TuneOutlined as OnboardingIcon,
  ChevronRightOutlined as ChevronRightIcon,
  HourglassTopOutlined as HourglassIcon,
} from '@mui/icons-material';
import { getCheckoutSummary } from '../../services/stripeApi';
import { getAppUrl } from '../../utils/deploymentMode';

// Marketing palette — keep in sync with LandingPage / DocsLayout so
// the post-checkout surface feels continuous with the rest of the
// public site rather than a generic confirmation modal.
const ACCENT      = '#0969da';
const ACCENT_DARK = '#0550ae';
const SUCCESS     = '#1a7f37';
const SUCCESS_BG  = '#dafbe1';
const BORDER      = '#d0d7de';
const SURFACE     = '#ffffff';
const SURFACE_ALT = '#f6f8fa';
const INK         = '#1f2328';
const INK_SOFT    = '#57606a';
const INK_DIM     = '#8c959f';
const LOGO_SRC    = '/assets/realops.png';

/**
 * Post-Checkout landing page.
 *
 * <p>Stripe redirects here with {@code ?session_id={CHECKOUT_SESSION_ID}};
 * the page renders a workspace-flavoured "you're in" confirmation,
 * a receipt-style summary, a 3-step onboarding teaser, and a primary
 * CTA into the platform login (with the workspace slug pre-filled).
 *
 * <p>Edge handling:
 * <ul>
 *   <li>{@code paymentStatus !== 'paid'} (webhook hasn't fired) — page
 *       still renders cleanly, but the headline switches to "Almost
 *       there" and the CTA copy explains the plan will activate
 *       within a few seconds.</li>
 *   <li>Missing / unrecognised {@code session_id} — generic "thanks"
 *       so the page never dead-ends.</li>
 * </ul>
 */
const formatAmount = (cents, currency) => {
  if (cents == null) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency?.toUpperCase() || ''}`.trim();
  }
};

const SignupSuccessPage = () => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const sessionId = search.get('session_id');

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getCheckoutSummary(sessionId)
      .then((s) => { if (!cancelled) setSummary(s); })
      .catch(() => { if (!cancelled) setSummary(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId]);

  const orgSlug          = summary?.orgSlug;
  const orgName          = summary?.orgName;
  const planName         = summary?.planName;
  const planDisplayPrice = summary?.planDisplayPrice;
  const customerEmail    = summary?.customerEmail;
  const amount           = formatAmount(summary?.amountTotal, summary?.currency);
  const isPaid           = summary?.paymentStatus === 'paid';
  const stillProcessing  = summary?.recognised && !isPaid;

  // Deep-link the login button to the platform with the org slug
  // pre-filled, so the user doesn't have to remember it.
  const loginHref = orgSlug
    ? `${getAppUrl('/')}?orgSlug=${encodeURIComponent(orgSlug)}`
    : getAppUrl('/');

  return (
    <Box sx={{ bgcolor: isDark ? '#0d1117' : SURFACE, color: isDark ? '#e6edf3' : INK, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* ── Top nav — mirrors LandingPage so the user feels continuous
            brand, not lost on a stranger surface. ─────────────────── */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: isDark ? 'rgba(13,17,23,0.92)' : 'rgba(255,255,255,0.92)',
          backdropFilter: 'saturate(180%) blur(8px)',
          borderBottom: '1px solid',
          borderColor: isDark ? '#30363d' : BORDER,
          color: isDark ? '#e6edf3' : INK,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: 64, gap: 3 }}>
            <RouterLink
              to="/"
              style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', gap: 10 }}
            >
              <Box
                component="img"
                src={LOGO_SRC}
                alt="VerOps"
                sx={{ height: 30, width: 'auto', display: 'block' }}
              />
            </RouterLink>
            <Box sx={{ flex: 1 }} />
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ display: { xs: 'none', sm: 'flex' } }}
            >
              <Link
                href="/docs"
                underline="none"
                sx={{ fontSize: 14, fontWeight: 500, color: isDark ? '#9da7b3' : INK_SOFT, '&:hover': { color: ACCENT } }}
              >
                Docs
              </Link>
              <Link
                href="/contact/new?type=GENERAL"
                underline="none"
                sx={{ fontSize: 14, fontWeight: 500, color: isDark ? '#9da7b3' : INK_SOFT, '&:hover': { color: ACCENT } }}
              >
                Contact
              </Link>
            </Stack>
            <Button
              component="a"
              href={loginHref}
              variant="contained"
              size="small"
              sx={{
                bgcolor: ACCENT,
                color: '#fff',
                fontWeight: 500,
                textTransform: 'none',
                px: 2.5,
                boxShadow: 'none',
                '&:hover': { bgcolor: ACCENT_DARK, boxShadow: 'none' },
              }}
              endIcon={<ChevronRightIcon fontSize="small" />}
            >
              Log in
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      {/* ── Hero — full-width band with subtle radial halo. ──────── */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          backgroundImage: isDark
            ? 'radial-gradient(circle at 50% -10%, rgba(46,160,67,0.18) 0%, rgba(13,17,23,0) 55%)'
            : 'radial-gradient(circle at 50% -10%, rgba(46,160,67,0.16) 0%, rgba(255,255,255,0) 55%)',
          py: { xs: 5, md: 8 },
        }}
      >
        <Container maxWidth="md">
          {/* ── Status pill — sets the tone before the headline. ──── */}
          <Stack alignItems="center" sx={{ mb: 3 }}>
            <Chip
              icon={stillProcessing ? <HourglassIcon sx={{ fontSize: 14 }} /> : <CheckIcon sx={{ fontSize: 14 }} />}
              label={stillProcessing ? 'Finalising payment' : 'Payment confirmed'}
              sx={{
                bgcolor: stillProcessing
                  ? (isDark ? 'rgba(212,167,44,0.16)' : '#fff8c5')
                  : (isDark ? 'rgba(46,160,67,0.18)' : SUCCESS_BG),
                color: stillProcessing ? '#9a6700' : SUCCESS,
                fontWeight: 700,
                px: 1,
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          </Stack>

          <Typography
            variant="h1"
            sx={{
              textAlign: 'center',
              fontWeight: 800,
              fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.4rem' },
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
              mb: 1.5,
            }}
          >
            {stillProcessing ? 'Almost there.' : 'Welcome to VerOps.'}
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              maxWidth: 620,
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.15rem' },
              color: isDark ? '#9da7b3' : INK_SOFT,
              lineHeight: 1.6,
              mb: 5,
            }}
          >
            {stillProcessing
              ? "Stripe is finalising your payment — your plan upgrades automatically within a few seconds. You can sign in now."
              : orgName
                ? `Your workspace ${orgName} is ready. Sign in to start correlating signal across every layer of your stack.`
                : 'Your workspace is ready. Sign in to start correlating signal across every layer of your stack.'}
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              {/* ── Receipt-style summary card ───────────────────── */}
              {summary?.recognised && (
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: isDark ? '#30363d' : BORDER,
                    borderRadius: 3,
                    overflow: 'hidden',
                    bgcolor: isDark ? '#0d1117' : SURFACE,
                  }}
                >
                  {/* Header strip */}
                  <Box
                    sx={{
                      px: { xs: 2.5, md: 3 },
                      py: 2,
                      bgcolor: isDark ? 'rgba(255,255,255,0.02)' : SURFACE_ALT,
                      borderBottom: '1px solid',
                      borderColor: isDark ? '#30363d' : BORDER,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                    }}
                  >
                    <ReceiptIcon sx={{ color: ACCENT, fontSize: 20 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="overline" sx={{ display: 'block', letterSpacing: '0.06em', fontWeight: 700, color: isDark ? '#9da7b3' : INK_SOFT, fontSize: '0.7rem', lineHeight: 1.2 }}>
                        Order summary
                      </Typography>
                      {sessionId && (
                        <Typography variant="caption" sx={{ color: isDark ? INK_DIM : INK_DIM, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          {sessionId.slice(0, 22)}…
                        </Typography>
                      )}
                    </Box>
                    {amount && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" sx={{ display: 'block', color: isDark ? '#9da7b3' : INK_SOFT, fontSize: '0.7rem' }}>
                          Charged
                        </Typography>
                        <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.01em', lineHeight: 1.1 }}>
                          {amount}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Detail rows */}
                  <Box sx={{ px: { xs: 2.5, md: 3 } }}>
                    {orgName && (
                      <DetailRow
                        icon={<OrgIcon sx={{ fontSize: 18 }} />}
                        label="Workspace"
                        isDark={isDark}
                      >
                        <Typography variant="body1" fontWeight={700}>{orgName}</Typography>
                        {orgSlug && (
                          <Typography variant="caption" sx={{ display: 'block', color: isDark ? INK_DIM : INK_SOFT, fontFamily: 'monospace', mt: 0.25 }}>
                            app.verops.io/{orgSlug}
                          </Typography>
                        )}
                      </DetailRow>
                    )}
                    {planName && (
                      <DetailRow
                        icon={<PlanIcon sx={{ fontSize: 18 }} />}
                        label="Plan"
                        isDark={isDark}
                        last={!customerEmail}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body1" fontWeight={700}>{planName}</Typography>
                          {planDisplayPrice && (
                            <Chip
                              label={planDisplayPrice}
                              size="small"
                              sx={{
                                bgcolor: isDark ? 'rgba(110,168,254,0.12)' : 'rgba(9,105,218,0.08)',
                                color: ACCENT,
                                fontWeight: 700,
                                height: 22,
                              }}
                            />
                          )}
                        </Stack>
                      </DetailRow>
                    )}
                    {customerEmail && (
                      <DetailRow
                        icon={<EmailIcon sx={{ fontSize: 18 }} />}
                        label="Receipt"
                        isDark={isDark}
                        last
                      >
                        <Typography variant="body1" fontWeight={700} sx={{ wordBreak: 'break-all' }}>
                          {customerEmail}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: isDark ? INK_DIM : INK_SOFT, mt: 0.25 }}>
                          Stripe sent a copy to this address.
                        </Typography>
                      </DetailRow>
                    )}
                  </Box>
                </Card>
              )}

              {/* ── 3-step "what's next" — numbered circles, descriptive copy ── */}
              <Card
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: isDark ? '#30363d' : BORDER,
                  borderRadius: 3,
                  p: { xs: 2.5, md: 3.5 },
                  bgcolor: isDark ? '#0d1117' : SURFACE,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <RocketIcon sx={{ color: ACCENT, fontSize: 20 }} />
                  <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: '-0.01em' }}>
                    What's next
                  </Typography>
                </Stack>
                <Stack spacing={2.5}>
                  <Step
                    n={1}
                    icon={<LoginIcon sx={{ fontSize: 18 }} />}
                    title="Sign in to your workspace"
                    body={
                      orgSlug
                        ? <>Use the email and password you just created. We'll route you straight to <code>{orgSlug}</code>.</>
                        : 'Use the email and password you just created.'
                    }
                    isDark={isDark}
                  />
                  <Step
                    n={2}
                    icon={<OnboardingIcon sx={{ fontSize: 18 }} />}
                    title="Walk through onboarding"
                    body="Connect your first agent or app — the wizard takes about three minutes and shows you data within seconds."
                    isDark={isDark}
                  />
                  <Step
                    n={3}
                    icon={<TeamIcon sx={{ fontSize: 18 }} />}
                    title="Invite your team"
                    body="Add teammates from Settings → Users. Operators see the same data; everyone gets per-role access."
                    isDark={isDark}
                  />
                </Stack>
              </Card>

              {/* ── Primary + secondary CTAs ─────────────────────── */}
              <Box>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  endIcon={<LoginIcon />}
                  href={loginHref}
                  sx={{
                    bgcolor: ACCENT,
                    color: '#fff',
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: '1rem',
                    py: 1.75,
                    boxShadow: '0 8px 24px rgba(9,105,218,0.28)',
                    '&:hover': { bgcolor: ACCENT_DARK, boxShadow: '0 8px 24px rgba(9,105,218,0.32)' },
                  }}
                >
                  Sign in to your workspace
                </Button>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  justifyContent="center"
                  alignItems="center"
                  sx={{ mt: 2 }}
                >
                  <Button
                    component={RouterLink}
                    to="/docs"
                    variant="text"
                    size="small"
                    startIcon={<DocsIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: 'none',
                      color: isDark ? '#9da7b3' : INK_SOFT,
                      '&:hover': { color: ACCENT, bgcolor: 'transparent' },
                    }}
                  >
                    Read the docs
                  </Button>
                  <Box sx={{ display: { xs: 'none', sm: 'block' }, color: isDark ? INK_DIM : INK_DIM }}>·</Box>
                  <Button
                    component={RouterLink}
                    to="/contact/new?type=GENERAL"
                    variant="text"
                    size="small"
                    startIcon={<HelpIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: 'none',
                      color: isDark ? '#9da7b3' : INK_SOFT,
                      '&:hover': { color: ACCENT, bgcolor: 'transparent' },
                    }}
                  >
                    Need help? Contact us
                  </Button>
                </Stack>
              </Box>

              {!sessionId && (
                <Card
                  elevation={0}
                  sx={{
                    border: '1px dashed',
                    borderColor: isDark ? '#30363d' : BORDER,
                    borderRadius: 2,
                    p: 2.5,
                    textAlign: 'center',
                    bgcolor: isDark ? 'rgba(255,255,255,0.015)' : SURFACE_ALT,
                  }}
                >
                  <Typography variant="body2" sx={{ color: isDark ? '#9da7b3' : INK_SOFT }}>
                    No checkout session id in the URL — if you just completed signup, sign in above.
                  </Typography>
                </Card>
              )}
            </Stack>
          )}
        </Container>
      </Box>

      {/* ── Footer — tiny, lives below the fold but anchors the
            brand and gives a credible-looking close. ───────────── */}
      <Box
        component="footer"
        sx={{
          borderTop: '1px solid',
          borderColor: isDark ? '#30363d' : BORDER,
          py: 3,
          bgcolor: isDark ? '#0d1117' : SURFACE,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            spacing={1.5}
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                component="img"
                src={LOGO_SRC}
                alt="VerOps"
                sx={{ height: 22, width: 'auto', opacity: 0.85 }}
              />
              <Typography variant="caption" sx={{ color: isDark ? INK_DIM : INK_SOFT }}>
                © {new Date().getFullYear()} VerOps. Observability that connects.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2.5}>
              <Link href="/" underline="none" sx={{ fontSize: 13, color: isDark ? '#9da7b3' : INK_SOFT, '&:hover': { color: ACCENT } }}>Home</Link>
              <Link href="/docs" underline="none" sx={{ fontSize: 13, color: isDark ? '#9da7b3' : INK_SOFT, '&:hover': { color: ACCENT } }}>Docs</Link>
              <Link href="/contact/new?type=GENERAL" underline="none" sx={{ fontSize: 13, color: isDark ? '#9da7b3' : INK_SOFT, '&:hover': { color: ACCENT } }}>Contact</Link>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

const DetailRow = ({ icon, label, children, isDark, last }) => (
  <Stack
    direction="row"
    alignItems="flex-start"
    spacing={2}
    sx={{
      py: 2,
      borderBottom: last ? 0 : '1px solid',
      borderColor: isDark ? '#21262d' : '#eaeef2',
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        flexShrink: 0,
        borderRadius: 1,
        bgcolor: isDark ? 'rgba(110,168,254,0.10)' : 'rgba(9,105,218,0.06)',
        color: ACCENT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 110 }}>
      <Typography variant="caption" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, fontSize: '0.7rem', color: isDark ? '#9da7b3' : INK_SOFT }}>
        {label}
      </Typography>
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {children}
    </Box>
  </Stack>
);

const Step = ({ n, icon, title, body, isDark }) => (
  <Stack direction="row" spacing={2}>
    <Box
      sx={{
        position: 'relative',
        width: 32,
        height: 32,
        flexShrink: 0,
        borderRadius: '50%',
        bgcolor: isDark ? 'rgba(9,105,218,0.18)' : 'rgba(9,105,218,0.08)',
        color: ACCENT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 800,
        fontSize: '0.85rem',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '100%',
          left: '50%',
          width: 1,
          height: 18,
          bgcolor: isDark ? '#30363d' : BORDER,
          transform: 'translateX(-50%)',
        },
        '&:last-of-type::after': { display: 'none' },
      }}
    >
      {n}
    </Box>
    <Box sx={{ flex: 1, pt: 0.25 }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        <Box sx={{ color: isDark ? '#9da7b3' : INK_SOFT, display: 'flex' }}>{icon}</Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: isDark ? '#9da7b3' : INK_SOFT, lineHeight: 1.55 }}>
        {body}
      </Typography>
    </Box>
  </Stack>
);

export default SignupSuccessPage;
